<template>
  <LoadingIndicator v-if="isLoading" />
  <ul v-else-if="customer.id" class="customer-info">
    <li>
      <div class="head">
        <p class="name">{{ toEllipsis(customerName, 20) }}</p>
        <div v-if="customer.visitCount || isMember" class="params">
          <MemberLabel v-if="isMember" />
          <VisitCount v-if="customer.visitCount" :visitCount="customer.visitCount" />
        </div>
      </div>
      <p class="option kana">{{ toEllipsis(customerNameRuby, 20) }}</p>
      <p v-if="customer.lastVisitDate" class="option last-visit">最終来店日：{{ lastVisitDate(customer) }}</p>
      <ul v-if="hasAttributes" class="attributes">
        <li class="label bad" v-for="value in customer.attributesList.bad" :key="value">{{ value }}</li>
        <li v-for="value in customer.attributesList.normal" class="label" :key="value">{{ value }}</li>
      </ul>
      <ul v-if="hasOtherAttributes" class="attributes">
        <li class="label other" v-for="value in customer.otherAttributes" :key="value.id">{{ value.name }}</li>
      </ul>
    </li>
    <li v-if="tel || customer.mail1 || customer.mail2">
      <p v-if="tel" class="option tel">{{ tel }}</p>
      <p v-if="customer.mail1" class="option mail">{{ customer.mail1 }}</p>
      <p v-if="customer.mail2" class="option mail">{{ customer.mail2 }}</p>
    </li>
    <li v-if="hasCompany">
      <p v-if="company.name" class="option company">{{ company.name }}</p>
      <p v-if="company.department" class="option space">{{ company.department }}</p>
      <p v-if="company.post" class="option space">{{ company.post }}</p>
      <p v-if="company.tel" class="option space">{{ company.tel }}</p>
      <p v-if="company.receiptAddress" class="option space">領収書宛名：{{ company.receiptAddress }}</p>
      <p v-if="company.remarks" class="option space">{{ company.remarks }}</p>
    </li>
    <li>
      <p v-if="customer.gender !== '指定なし'" class="option gender">{{ customer.gender }}</p>
      <p v-if="customer.smoking !== '指定なし'" class="option smoke">{{ customer.smoking }}</p>
      <dl>
        <template v-if="customer.allergies">
          <dt>アレルギー</dt>
          <dd>{{ customer.allergies }}</dd>
        </template>
        <template v-if="customer.favoriteFood">
          <dt>好きな料理</dt>
          <dd>{{ customer.favoriteFood }}</dd>
        </template>
        <template v-if="customer.notFavoriteFood">
          <dt>苦手な料理</dt>
          <dd>{{ customer.notFavoriteFood }}</dd>
        </template>
        <template v-if="customer.customerRemarks">
          <dt>お客様メモ</dt>
          <dd>{{ customer.customerRemarks }}</dd>
        </template>
        <template v-if="customer.remarks">
          <dt>備考</dt>
          <dd>{{ customer.remarks }}</dd>
        </template>
      </dl>
    </li>
  </ul>
</template>

<script>
import { toEllipsis } from '@/utils/string'
import { formatDate } from '@/utils/date'
import MemberLabel from '@/components/atoms/MemberLabel'
import VisitCount from '@/components/molecules/VisitCount'
import LoadingIndicator from '@/components/molecules/LoadingIndicator'

/**
 *  顧客情報の表示
 *
 *  顧客情報の一部を表示します。
 */
export default {
  name: 'CustomerInfo',
  components: {
    MemberLabel,
    VisitCount,
    LoadingIndicator,
  },
  props: {
    /** 表示対象の顧客データ */
    customer: { type: Object },
    /** ローディング表示（true:表示,false:非表示）*/
    isLoading: { type: Boolean, default: false },
  },
  computed: {
    customerName() {
      const { tel1, tel2, name: { last, first, lastKana, firstKana } = {} } = this.customer
      if (last || first) {
        return `${last || ''} ${first || ''}`
      } else if (lastKana || firstKana) {
        return `${lastKana || ''} ${firstKana || ''}`
      }
      return tel1 || tel2
    },
    customerNameRuby() {
      const { name: { last, first, lastKana, firstKana } = {} } = this.customer
      return last || first ? `${lastKana || ''} ${firstKana || ''}` : null
    },
    tel() {
      const { tel1, tel2 } = this.customer
      return tel1 && tel2 ? `${tel1} / ${tel2}` : tel1 || tel2
    },
    hasAttributes() {
      if (!this.customer.attributesList) return false
      const { bad, normal } = this.customer.attributesList
      return (bad && bad.length) || (normal && normal.length)
    },
    hasOtherAttributes() {
      return this.customer.otherAttributes && this.customer.otherAttributes.length
    },
    company() {
      return this.customer.company
    },
    hasCompany() {
      return this.company && Object.keys(this.company).length
    },
    isMember() {
      return !!this.customer.syncMemberType
    },
  },
  methods: {
    toEllipsis,
    lastVisitDate({ lastVisitDate }) {
      const [date, time] = lastVisitDate.split(' ')
      return `${formatDate(date, 'YYYY/M/D (dd)')} ${time.replace(':', '：')}`
    },
  },
}
</script>

<style lang="postcss" scoped>
.customer-info {
  > li {
    padding-bottom: 8px;

    & + li {
      border-top: 1px solid var(--very-light-gray-2);
    }
  }

  .responsive & {
    @media screen and (--sp-modal) {
      > li {
        padding: 8px 0 16px;
      }
    }
  }
}

.head {
  align-items: center;
  display: grid;
  grid-auto-flow: column;
}

.params {
  display: grid;
  grid-auto-flow: column;
  justify-self: flex-end;
}

.name {
  font-size: 21px;
  font-weight: bold;
  line-height: 1;

  .responsive & {
    @media screen and (--sp-modal) {
      font-size: 20px;
    }
  }

  &::after {
    content: '様';
    font-size: 15px;
    font-weight: normal;
    margin-left: 8px;

    .responsive & {
      @media screen and (--sp-modal) {
        font-size: 12px;
      }
    }
  }
}

.option {
  font-size: 13px;
  line-height: 16px;
  margin-top: 8px;

  &.space {
    padding-left: 24px;
  }

  &.tel {
    background: url(/assets/img/icons/customer_tel.png) no-repeat left center;
    background-size: 16px 16px;
    padding-left: 24px;
  }

  &.mail {
    background: url(/assets/img/icons/customer_mail.png) no-repeat left center;
    background-size: 16px 16px;
    padding-left: 24px;
  }

  &.company {
    background: url(/assets/img/icons/customer_company.png) no-repeat left center;
    background-size: 16px 16px;
    padding-left: 24px;
  }

  &.gender {
    background: url(/assets/img/icons/customer_gender.png) no-repeat left center;
    background-size: 16px 16px;
    padding-left: 24px;
  }

  &.smoke {
    background: url(/assets/img/icons/customer_smoke.png) no-repeat left center;
    background-size: 16px 16px;
    padding-left: 24px;
  }

  .responsive &.kana {
    @media screen and (--sp-modal) {
      font-size: 11px;
      margin-top: 4px;
    }
  }

  .responsive &:not(.last-visit, .kana) {
    @media screen and (--sp-modal) {
      font-size: 15px;
    }
  }
}

.attributes {
  display: flex;
  flex-wrap: wrap;

  &:first-of-type {
    margin-top: 10px;
  }

  .label {
    background-color: var(--pure-orange);
    box-sizing: border-box;
    color: var(--white);
    font-size: 13px;
    height: 16px;
    line-height: 1.34;
    margin-right: 8px;
    margin-top: 6px;
    min-width: 48px;
    padding: 0 4px;
    text-align: center;

    &.bad {
      background-color: var(--pure-red);
    }

    &.other {
      background-color: var(--dark-gray);
      overflow: hidden;
    }
  }

  .responsive & {
    @media screen and (--sp-modal) {
      .label {
        font-size: 15px;
        height: 24px;
        line-height: 1.7;
      }
    }
  }
}

dl {
  display: grid;
  font-size: 13px;
  grid-gap: 8px 0;
  grid-template-columns: auto 1fr;
  line-height: 16px;
  margin-top: 8px;

  .responsive & {
    @media screen and (--sp-modal) {
      font-size: 15px;
    }
  }
}

dt {
  display: flex;
  justify-content: space-between;

  .responsive & {
    @media screen and (--sp-modal) {
      font-weight: bold;
    }
  }

  &::after {
    content: '：';
  }
}

dd {
  white-space: pre-wrap;
}
</style>
